import { supabase } from './client';
import { SERVICE_STATUSES, TRACKING_STATUSES } from '../../constants';

// ========================
// CONSULTAS DE UI (SERVICIOS / CHAT / EVIDENCIA / ROLES)
// Extraídas de componentes para centralizar acceso a Supabase.
// Todas devuelven { data, error } salvo indicación contraria.
// ========================

/**
 * Servicio activo (searching/asignado/en curso/espera) para un usuario
 * según su rol en el servicio. Devuelve maybeSingle().
 */
export async function getActiveServiceForUser(userId, roleField = 'user_uid') {
  let query = supabase
    .from('services')
    .select('*')
    .in('status', [
      SERVICE_STATUSES.SEARCHING,
      SERVICE_STATUSES.DRIVER_ASSIGNED,
      SERVICE_STATUSES.IN_PROGRESS,
      SERVICE_STATUSES.AWAITING_CONFIRMATION,
    ])
    .order('created_at', { ascending: false })
    .limit(1);

  if (roleField) {
    query = query.eq(roleField, userId);
  }

  return query.maybeSingle();
}

/**
 * Nombre de perfil por id de usuario.
 */
export async function getProfileName(profileId) {
  return supabase
    .from('profiles')
    .select('name')
    .eq('id', profileId)
    .maybeSingle();
}

/**
 * Vehículo por id.
 */
export async function getVehicleById(vehicleId) {
  return supabase
    .from('vehicles')
    .select('*')
    .eq('id', vehicleId)
    .maybeSingle();
}

/**
 * Vehículo por defecto de un conductor.
 */
export async function getDefaultVehicle(driverUid) {
  return supabase
    .from('vehicles')
    .select('*')
    .eq('driver_uid', driverUid)
    .eq('is_default', true)
    .maybeSingle();
}

/**
 * Marca al conductor como "en viaje" en drivers_tracking.
 */
export async function setDriverOnTrip(driverUid) {
  return supabase.from('drivers_tracking').upsert(
    {
      driver_uid: driverUid,
      status: TRACKING_STATUSES.ON_TRIP,
      last_updated: new Date().toISOString(),
    },
    { onConflict: 'driver_uid' }
  );
}

/**
 * Catálogo de categorías activas con sus subcategorías.
 */
export async function getServiceCategories() {
  return supabase
    .from('service_categories')
    .select('*, subcategories:service_subcategories(*)')
    .eq('is_active', true)
    .order('sort_order')
    .order('name');
}

/**
 * Reinicia created_at de un servicio buscando (extender timeout).
 */
export async function resetSearchingTimeout(serviceId) {
  return supabase
    .from('services')
    .update({ created_at: new Date().toISOString() })
    .eq('service_id', serviceId);
}

// ========================
// EVIDENCIA DE ENTREGA (STORAGE)
// ========================

const DELIVERY_EVIDENCE_BUCKET = 'delivery_evidence';

/**
 * Sube una foto de evidencia y devuelve la URL pública.
 * @returns {Promise<{ url: string|null, error: any }>}
 */
export async function uploadDeliveryEvidence(serviceId, type, file) {
  const fileExt = file.name.split('.').pop();
  const fileName = `${serviceId}_${type}_${Date.now()}.${fileExt}`;
  const filePath = `${serviceId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from(DELIVERY_EVIDENCE_BUCKET)
    .upload(filePath, file);

  if (uploadError) return { url: null, error: uploadError };

  const { data: publicUrlData } = supabase.storage
    .from(DELIVERY_EVIDENCE_BUCKET)
    .getPublicUrl(filePath);

  return { url: publicUrlData.publicUrl, error: null };
}

/**
 * Guarda la URL de evidencia (pickup/dropoff) en el servicio.
 */
export async function setServiceEvidenceUrl(serviceId, type, url) {
  const updateData =
    type === 'pickup'
      ? { pickup_evidence_url: url }
      : { dropoff_evidence_url: url };

  return supabase
    .from('services')
    .update(updateData)
    .eq('service_id', serviceId);
}

// ========================
// CONDUCTOR / REGISTRO
// ========================

/**
 * Estado del registro de conductor (drivers.status).
 */
export async function getDriverStatus(driverUid) {
  return supabase
    .from('drivers')
    .select('status')
    .eq('driver_uid', driverUid)
    .maybeSingle();
}

/**
 * Tipo de vehículo del vehículo por defecto del conductor.
 */
export async function getDefaultVehicleType(driverUid) {
  return supabase
    .from('vehicles')
    .select('vehicle_type')
    .eq('driver_uid', driverUid)
    .eq('is_default', true)
    .maybeSingle();
}

/**
 * Perfil público completo del conductor (tabla drivers).
 */
export async function getDriverByUid(driverUid) {
  return supabase
    .from('drivers')
    .select('*')
    .eq('driver_uid', driverUid)
    .maybeSingle();
}

/**
 * Perfil base de usuario (profiles).
 */
export async function getUserProfile(profileId) {
  return supabase
    .from('profiles')
    .select('id, name, email, phone, role, rating, avatar_url, created_at')
    .eq('id', profileId)
    .maybeSingle();
}

/**
 * Id de rol por nombre (tabla roles).
 */
export async function getRoleIdByName(roleName) {
  return supabase
    .from('roles')
    .select('id')
    .eq('name', roleName)
    .maybeSingle();
}

/**
 * Upsert de asignación de rol a usuario (inactivo hasta aprobación).
 */
export async function assignRoleToUser(userId, roleId, assignedBy) {
  return supabase
    .from('user_roles')
    .upsert(
      {
        user_id: userId,
        role_id: roleId,
        is_active: false,
        assigned_by: assignedBy,
      },
      { onConflict: 'user_id,role_id' }
    );
}

/**
 * Sincroniza el campo role del perfil del usuario.
 */
export async function setUserProfileRole(profileId, roleName) {
  return supabase
    .from('profiles')
    .update({ role: roleName })
    .eq('id', profileId);
}

// ========================
// CHAT EN VIVO
// ========================

/**
 * Mensajes de un servicio ordenados cronológicamente.
 */
export async function getChatMessages(serviceId) {
  return supabase
    .from('chat_messages')
    .select('*')
    .eq('service_id', serviceId)
    .order('created_at', { ascending: true });
}

/**
 * Marca varios mensajes como leídos por id.
 */
export async function markChatMessagesRead(messageIds) {
  return supabase
    .from('chat_messages')
    .update({ is_read: true })
    .in('id', messageIds);
}

/**
 * Marca un mensaje como leído por id.
 */
export async function markChatMessageRead(messageId) {
  return supabase
    .from('chat_messages')
    .update({ is_read: true })
    .eq('id', messageId);
}

/**
 * Inserta un mensaje de chat.
 */
export async function sendChatMessage(serviceId, senderUid, message) {
  return supabase.from('chat_messages').insert([
    {
      service_id: serviceId,
      sender_uid: senderUid,
      message,
    },
  ]);
}

// ========================
// CONFIGURACIÓN DE TARIFAS (usado por tarifaCalculator)
// ========================

/**
 * Config de tarifas activa más reciente (tariffs_config).
 */
export async function getActiveTariffRow() {
  return supabase
    .from('tariffs_config')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
}

/**
 * Valor JSON de platform_commissions desde platform_config.
 */
export async function getPlatformCommissionsRaw() {
  return supabase
    .from('platform_config')
    .select('value')
    .eq('key', 'platform_commissions')
    .maybeSingle();
}

/**
 * Tarifas de una subcategoría de servicio por id.
 */
export async function getServiceSubcategoryTariff(subcategoryId) {
  return supabase
    .from('service_subcategories')
    .select('base_price, price_per_km, price_per_min, min_price')
    .eq('id', subcategoryId)
    .single();
}
